import moment from 'moment';
import JSZip from 'jszip';
import { Component, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { CustomDateTimeCriteriaType, FlowInstancesByInbox, FlowInstancesByInboxDescription, FlowInstancesByInboxLabel, DashboardSortState, FlowInfoFilterGroup, InputDataTaskForm, SearchCriteriaType, SearchToken, FlowInstancesByInboxCsvDescription } from '../../../models/dashboard.model';
import { FlowDefinition } from '../../../models/flow.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { Utils } from '../../../shared/utils';
import { Unidade } from '../../../models/organograma.model';
import { DashboardService } from '../../../services/dashboard.service';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { IMPORTS, PROVIDERS } from './_imports';
import { AcessoCidadaoService } from '../../../services/acesso-cidadao.service';

@Component({
    selector: 'flow-instances-by-inbox',
    templateUrl: './flow-instances-by-inbox.component.html',
    styleUrl: './flow-instances-by-inbox.component.scss',
    standalone: true,
    imports: IMPORTS,
    providers: PROVIDERS
})
export class FlowInstancesByInboxComponent implements OnInit {
    // #region [ViewChild]
    @ViewChild('flowInstanceResultsAreaRef') flowInstanceResultsAreaRef: ElementRef;
    // #endregion

    // #region [Type props]
    SearchCriteriaType = SearchCriteriaType;
    CustomDateTimeCriteriaType = CustomDateTimeCriteriaType;
    FlowInstancesByInboxLabel = FlowInstancesByInboxLabel;
    FlowInstancesByInboxDescription = FlowInstancesByInboxDescription;
    // #endregion

    // #region [const]
    selectedFlowInboxConfig: any = {
        search: true,
        searchOnKey: 'description',
        displayKey: 'description',
        placeholder: 'Buscar',
        searchPlaceholder: 'Filtrar',
        moreText: 'Mais',
        noResultsFound: 'Nenhum resultado encontrado',
        height: '500px'
    } as const;
    // #endregion

    // #region [props]
    filteredFlowDefinitions: FlowDefinition[] = [];
    searchTokens: SearchToken[] = [];
    sortState: DashboardSortState = new DashboardSortState();
    customFilterSearch: string = null;
    adminUnitsList: Unidade[] = [];
    flowInfoTypes: SearchCriteriaType[] = [
        SearchCriteriaType.FlowDefinition,
        SearchCriteriaType.FlowInbox,
        SearchCriteriaType.Version
    ];
    finishDateTypes: SearchCriteriaType[] = [
        SearchCriteriaType.FinishDateFrom,
        SearchCriteriaType.FinishDateUntil
    ];
    // #endregion

    // #region [signals]
    $filteredResults = signal(null as FlowInstancesByInbox);
    $filteredVersions = signal([] as number[]);
    $selectedFlowInbox = signal(null as string);
    $selectedFlowInboxObject = signal(null as any);
    $selectedFlowDefinition = signal(null as string);
    $selectedVersion = signal(null as number);
    $selectedFromDateTime = signal(null as any);
    $selectedUntilDateTime = signal(null as any);
    $percentagePerWeekdayResults = signal([] as any[]);
    $percentagePerDayHourResults = signal([] as any[]);
    $isShowingFlowInfoSection = signal(true);
    $isShowingFinishDateSection = signal(true);
    // #endregion

    constructor(
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private dashboardService: DashboardService,
        private authService: AuthService,
        private acessoCidadaoService: AcessoCidadaoService,
    ) { }

    async ngOnInit() {
        const response = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        this.adminUnitsList = response.data;
    }

    async addFlowInfoFilterCriteria() {
        if (this.$selectedFlowInbox() == null) return;

        let now = new Date();

        let flowInbox = this.adminUnitsList.find(x => x.guid == this.$selectedFlowInbox());
        this.searchTokens.push(new SearchToken({
            searchCriteriaType: SearchCriteriaType.FlowInbox,
            addedTimestamp: now,
            key: this.$selectedFlowInbox(),
            text: `Caixa: ${flowInbox.organizacao.sigla} - ${flowInbox.nomeCurto}`
        }));

        if (this.$selectedFlowDefinition() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.FlowDefinition,
                addedTimestamp: now,
                key: this.$selectedFlowDefinition(),
                text: `Fluxo: ${this.filteredFlowDefinitions.find(x => x.id == this.$selectedFlowDefinition()).name}`
            }));
        }

        if (this.$selectedVersion() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.Version,
                addedTimestamp: now,
                key: this.$selectedVersion().toString(),
                text: `Versão: ${this.$selectedVersion()}`
            }));
        }

        this.searchTokens = [...new Set(this.searchTokens)];
        this.clearFlowInfoFilterCriteria();
    }

    clearFlowInfoFilterCriteria() {
        this.$selectedFlowDefinition.set(null);
        this.$selectedFlowInbox.set(null);
        this.$selectedVersion.set(null);
    }

    addPredefinedFinishDateFilterCriteria() {
        if (this.$selectedFromDateTime() == null && this.$selectedUntilDateTime() == null) return;

        this.searchTokens = this.searchTokens.filter(x =>
            ![
                SearchCriteriaType.FinishDateFrom,
                SearchCriteriaType.FinishDateUntil
            ].includes(x.searchCriteriaType)
        );

        if (this.$selectedFromDateTime() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                key: this.$selectedFromDateTime(),
                text: `De: ${moment.utc(this.$selectedFromDateTime()).format('DD/MM/YYYY, HH:mm:ss')}`
            }));
        }

        if (this.$selectedUntilDateTime() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                key: this.$selectedUntilDateTime(),
                text: `Até: ${moment.utc(this.$selectedUntilDateTime()).format('DD/MM/YYYY, HH:mm:ss')}`
            }));
        }

        this.searchTokens = [...new Set(this.searchTokens)];
        this.clearFinishDateFilterCriteria();
    }

    addCustomFinishDateFilterCriteria(type: CustomDateTimeCriteriaType) {
        this.searchTokens = this.searchTokens.filter(x =>
            ![
                SearchCriteriaType.FinishDateFrom,
                SearchCriteriaType.FinishDateUntil
            ].includes(x.searchCriteriaType)
        );

        let now = moment();
        let from = moment(now);
        let until = moment(now);

        switch (type) {
            case CustomDateTimeCriteriaType.Today:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').utc().set({ h: 0, m: 0, s: 0, ms: 0 }).toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last24:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-24, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last48:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-48, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last72:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-72, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastWeek:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'w').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastFortnight:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-15, 'd').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastMonth:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'M').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastYear:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'y').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            default:
                break;
        }

        this.searchTokens = [...new Set(this.searchTokens)];
    }

    clearFinishDateFilterCriteria() {
        this.$selectedFromDateTime.set(null);
        this.$selectedUntilDateTime.set(null);
    }

    removeFilter(searchToken: SearchToken) {
        if (this.finishDateTypes.includes(searchToken.searchCriteriaType)) {
            this.searchTokens = this.searchTokens.filter(x => !this.finishDateTypes.includes(x.searchCriteriaType));
        } else {
            this.searchTokens = this.searchTokens.filter(x => x != searchToken);
        }
    }

    getSearchTokensFlowInfo(timestamp?): SearchToken[] {
        return this.searchTokens.filter(x => this.flowInfoTypes.includes(x.searchCriteriaType)
            && (timestamp != null
                ? x.addedTimestamp == timestamp
                : true
            )
        );
    }

    getSearchTokensFinishDate(): SearchToken[] {
        return this.searchTokens.filter(x => this.finishDateTypes.includes(x.searchCriteriaType));
    }

    getFlowInfoTimestamps(): any[] {
        return [...new Set(this.getSearchTokensFlowInfo().map(x => x.addedTimestamp))];
    }

    getOrderedFlowInboxesList(): Unidade[] {
        return this.adminUnitsList.sort((a, b) => {
            let first = `${a.organizacao.sigla} - ${a.nomeCurto}`;
            let second = `${b.organizacao.sigla} - ${b.nomeCurto}`;
            return first.localeCompare(second);
        }).map(x => Object.assign(x, {
            description: `${x.organizacao.organizacaoPai?.sigla != null ? x.organizacao.organizacaoPai.sigla + ' - ' : ''}${x.organizacao.sigla} - ${x.nomeCurto}`
        }));
    }

    getOrderedFlowDefinitionsList(): FlowDefinition[] {
        return this.filteredFlowDefinitions.sort((a, b) => a.name.localeCompare(b.name));
    }

    async changeFlowInbox(event) {
        this.$selectedFlowInboxObject.set(event);
        this.$selectedFlowInbox.set(event.guid);

        this.$selectedFlowDefinition.set(null);
        this.$selectedVersion.set(null);

        const response = await this.dashboardService.getFlowDefinitionsByFlowInboxId(this.$selectedFlowInbox());

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.filteredFlowDefinitions = response.data;
    }

    getVersions(event) {
        this.$selectedFlowDefinition.set(event);

        this.$selectedVersion.set(null);

        let flowDefinition = this.filteredFlowDefinitions.find(x => x.id == this.$selectedFlowDefinition());
        this.$filteredVersions.set([...Array(flowDefinition.version + 1).keys()].slice(1).reverse());
    }

    shouldDisableSearch(): boolean {
        return !this.searchTokens.some(x => x.searchCriteriaType == SearchCriteriaType.FlowInbox);
    }

    toggleFlowInfoSection() {
        this.$isShowingFlowInfoSection.set(!this.$isShowingFlowInfoSection());
    }

    toggleFinishDateSection() {
        this.$isShowingFinishDateSection.set(!this.$isShowingFinishDateSection());
    }

    async getResults() {
        if (!this.isFilterValid()) return;

        this.spinner.show();

        this.$isShowingFlowInfoSection.set(false);
        this.$isShowingFinishDateSection.set(false);
        let cnt = 0;
        let interval = setInterval(() => {
            cnt++;
            if (this.flowInstanceResultsAreaRef != null || cnt == 30) {
                if (this.flowInstanceResultsAreaRef != null) {
                    Utils.scrollTo(this.flowInstanceResultsAreaRef.nativeElement.id);
                }

                clearInterval(interval);
            }
        }, 100);

        const response = await this.dashboardService.getResultsFlowInstancesByInbox({
            flowInfoGroups: this.getFlowInfoGroups(),
            fromDateTime: this.getFromDateTime(),
            untilDateTime: this.getUntilDateTime()
        });

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.loadFilteredResults(response.data);

        setTimeout(() => this.spinner.hide(), 500);
    }

    async exportCsv() {
        if (!this.isFilterValid()) return;

        this.spinner.show();

        try {
            const response = await this.dashboardService.exportCsvFlowInstancesByInbox({
                flowInfoGroups: this.getFlowInfoGroups(),
                fromDateTime: this.getFromDateTime(),
                untilDateTime: this.getUntilDateTime()
            });

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                this.spinner.hide();
                return;
            }

            const zip = new JSZip();
            let groups = [...new Set(response.data.map(x => `${x.flowDefinitionId}||${x.version}`))];

            groups.forEach(y => {
                let headers = Object.keys(response.data[0]).slice(1).map(x => FlowInstancesByInboxCsvDescription.get(x));
                const splitted = y.split('||');
                let flattenedData = Utils.cloneArray(response.data.filter(x => x.flowDefinitionId == splitted[0] && x.version == +splitted[1]));
                for (let i = 0; i < flattenedData.length; i++) {
                    flattenedData[i].inputDataParsed = JSON.parse(flattenedData[i].inputData) as InputDataTaskForm;
                    flattenedData[i] = Object.assign(flattenedData[i], flattenedData[i].inputDataParsed.data);

                    delete flattenedData[i].flowDefinitionId;
                    delete flattenedData[i].inputData;
                    delete flattenedData[i].inputDataParsed;
                    delete flattenedData[i].submit;
                    delete flattenedData[i].enviar;

                    // remoção de valores de campos marcados com a tag para "ignorar exportação"
                    let keysToBeRemoved = Object.keys(flattenedData[i]).filter(x => x.endsWith('_-xxx'));
                    for (let item of keysToBeRemoved) {
                        delete flattenedData[i][item];
                    }

                    const values = Object.values(flattenedData[i]);
                    values.forEach((x: any, j: number) => {
                        if (Utils.isObject(x) && x.fileName != null) {
                            // caso o campo seja do tipo "Arquivo(s) PDF"
                            flattenedData[i][Object.keys(flattenedData[i])[j]] = x.fileName;
                        } else if (Array.isArray(x)) {
                            if (x.length == 0) {
                                flattenedData[i][Object.keys(flattenedData[i])[j]] = '';
                            } else {
                                if (Utils.isObject(x[0])) {
                                    // caso o campo seja de um tipo derivado de tabelas
                                    flattenedData[i][Object.keys(flattenedData[i])[j]] = JSON.stringify(x);
                                } else {
                                    // caso o campo seja do tipo "Lista de opção (múltipla)"
                                    flattenedData[i][Object.keys(flattenedData[i])[j]] = x.join(', ');
                                }
                            }
                        } else if (Utils.isString(x)) {
                            // caso o campo seja de um tipo derivado de numérico
                            flattenedData[i][Object.keys(flattenedData[i])[j]] = x.replace('R$ NaN', '').replace('NaN', '');
                        } else if (Utils.isObject(x)) {
                            // failsafe
                            if (Object.keys(x).length == 0) {
                                flattenedData[i][Object.keys(flattenedData[i])[j]] = '';
                            } else {
                                flattenedData[i][Object.keys(flattenedData[i])[j]] = JSON.stringify(x);
                            }
                        }
                    });

                    // os X primeiros campos de metadados (duplicados) são removidos
                    headers = headers.concat(Object.keys(flattenedData[i]).slice(12));
                }

                const csvOptions = {
                    fieldSeparator: ';',
                    showLabels: true,
                    headers: [...new Set(headers)].filter(x => x != null),
                    nullToEmptyString: true,
                    noDownload: true
                };
                const splittedTime = (new Date()).toLocaleString().split(', ');
                const timeString = splittedTime[0].split('/').reverse().join('') + '_' + splittedTime[1].replaceAll(':', '');
                const filename = `${flattenedData[0].name} v${flattenedData[0].version} (${timeString})`.replaceAll('/', '-').replaceAll('\\', '-');
                const csv = new AngularCsv(flattenedData, filename, csvOptions);
                zip.file(`${filename}.csv`, csv.getCsvData());
            });

            zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: { level: 9 }
            }).then(content => {
                let a = document.createElement('a');
                a.href = window.URL.createObjectURL(content);
                const splittedTime = (new Date()).toLocaleString().split(', ');
                const timeString = splittedTime[0].split('/').reverse().join('') + '_' + splittedTime[1].replaceAll(':', '');
                a.download = `E-Flow Fluxos Instanciados por Caixa de Fluxo (${timeString}).zip`;
                a.click();
            }).catch(() => {
                this.toastr.error(Enums.Messages.CsvExportError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            }).finally(() => {
                this.spinner.hide();
            });
        } catch (error) {
            this.toastr.error(Enums.Messages.CsvExportError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            this.spinner.hide();
            console.error(error);
        }
    }

    openFlowInbox() {
        setTimeout(() => {
            (document.querySelector('ngx-select-dropdown .search-container > input') as HTMLElement).focus();
        }, 1);
    }

    // #region [===== private methods =====]
    // #endregion

    private loadFilteredResults(responseData: FlowInstancesByInbox) {
        Utils.setSignal(this.$filteredResults, responseData);

        Utils.updSignal(this.$filteredResults, x => {
            x.averageFinishedFlowInstancesDay = Utils.round(x.averageFinishedFlowInstancesDay, 1);
            return x;
        });

        let value = this.$filteredResults().averageFlowInstanceFinishingTimeSeconds || 0;
        Utils.updSignal(this.$filteredResults, x => {
            if (value < 2 * 60) { /* 2min */
                x.averageFlowInstanceFinishingTimeSecondsProcessed = Utils.round(value, 1).toFixed(1) + 's';
            } else if (value < 2 * 60 * 60) { /* 2h */
                x.averageFlowInstanceFinishingTimeSecondsProcessed = Utils.round(value / 60, 1).toFixed(1) + 'min';
            } else if (value < 2 * 24 * 60 * 60) { /* 2d */
                x.averageFlowInstanceFinishingTimeSecondsProcessed = Utils.round(value / 60 / 60, 1).toFixed(1) + 'h';
            } else {
                x.averageFlowInstanceFinishingTimeSecondsProcessed = Utils.round(value / 60 / 60 / 24, 1).toFixed(1) + 'd';
            }
            return x;
        });

        this.loadPercentageResults();
    }

    private loadPercentageResults() {
        this.$percentagePerWeekdayResults.set([]);
        let weekdayProps = Object.keys(this.$filteredResults()).filter(x => x.toLowerCase().includes('weekday'));
        weekdayProps.forEach(x => {
            Utils.updSignal(this.$percentagePerWeekdayResults, xx => {
                xx.push({
                    name: FlowInstancesByInboxLabel.get(x),
                    value: this.$filteredResults()[x]
                });
                return xx;
            });
        });

        this.$percentagePerDayHourResults.set([]);
        let dayHourProps = Object.keys(this.$filteredResults()).filter(x => x.toLowerCase().includes('hour'));
        dayHourProps.forEach(x => {
            Utils.updSignal(this.$percentagePerDayHourResults, xx => {
                xx.push({
                    name: FlowInstancesByInboxLabel.get(x),
                    value: this.$filteredResults()[x]
                });
                return xx;
            });
        });
    }

    private isFilterValid(): boolean {
        if (!this.searchTokens.some(x => x.searchCriteriaType == SearchCriteriaType.FlowInbox)) {
            this.toastr.warning(Enums.Messages.NoFlowInboxId, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        return true;
    }

    private getFlowInfoGroups(): FlowInfoFilterGroup[] {
        let timestamps = this.getFlowInfoTimestamps();
        let flowInfoGroups = [] as FlowInfoFilterGroup[];
        timestamps.forEach(x => {
            let group = this.searchTokens.filter(y => y.addedTimestamp == x);
            flowInfoGroups.push(new FlowInfoFilterGroup({
                flowInboxId: group.find(y => y.searchCriteriaType == SearchCriteriaType.FlowInbox)?.key,
                flowDefinitionId: group.find(y => y.searchCriteriaType == SearchCriteriaType.FlowDefinition)?.key,
                version: group.find(y => y.searchCriteriaType == SearchCriteriaType.Version)?.key
            }));
        });

        return flowInfoGroups;
    }

    private getFromDateTime(): string {
        return ((this.searchTokens.find(x => x.searchCriteriaType == SearchCriteriaType.FinishDateFrom)?.key || moment({ y: 2000, M: 6, d: 1 }).utc().toISOString())
            .split('.')[0] + 'Z').replaceAll('ZZ', 'Z');
    }

    private getUntilDateTime(): string {
        return ((this.searchTokens.find(x => x.searchCriteriaType == SearchCriteriaType.FinishDateUntil)?.key || moment({ y: 3000, M: 6, d: 1 }).utc().toISOString())
            .split('.')[0] + 'Z').replaceAll('ZZ', 'Z');
    }
}
